/**
 * <BuyerNotificationPrefCard buyerId={id} initialPref={pref} onChanged={cb} />
 *
 * Community Layer 3 — per-buyer notification preference for buy-box match
 * alerts. When a new lead matches one of this buyer's buy-boxes, we fire
 * an email (via Resend) and/or SMS (via Twilio) based on this setting.
 *
 * Endpoint:  PATCH /api/team/{id}  body { notification_pref: "email" | "sms" | "both" | "off" }
 */
import { useState } from "react";
import { Bell, Mail, MessageSquare, BellOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const OPTIONS = [
  { value: "email", label: "Email",  icon: Mail,           hint: "Resend HTML deal preview" },
  { value: "sms",   label: "SMS",    icon: MessageSquare,  hint: "Twilio text with link" },
  { value: "both",  label: "Both",   icon: Bell,           hint: "Email + SMS (recommended)" },
  { value: "off",   label: "Off",    icon: BellOff,        hint: "No alerts — still tracked" },
];

export default function BuyerNotificationPrefCard({ buyerId, initialPref, onChanged }) {
  const [pref, setPref] = useState((initialPref || "email").toLowerCase());
  const [saving, setSaving] = useState(false);

  const onChange = async (next) => {
    if (next === pref) return;
    const previous = pref;
    setPref(next);                 // optimistic
    setSaving(true);
    try {
      await api.patch(`/team/${buyerId}`, { notification_pref: next });
      toast.success(`Match alerts: ${OPTIONS.find((o) => o.value === next)?.label}`);
      onChanged?.(next);
    } catch (e) {
      setPref(previous);           // revert on failure
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card mb-5" data-testid="buyer-notification-pref-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5">
            <Bell size={11} className="text-rose-600" /> Match alerts
          </div>
          <div className="font-display font-bold text-lg mt-0.5">
            When a deal matches this buyer's buy-box…
          </div>
          <div className="text-xs text-[var(--ink-3)]">
            Notify the buyer the moment a fitting lead lands. First-look goes to first-reply.
          </div>
        </div>
        {saving && <Loader2 size={14} className="animate-spin text-slate-400" />}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="buyer-notification-pref-options">
        {OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const active = pref === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              disabled={saving}
              onClick={() => onChange(opt.value)}
              className={`text-left p-3 rounded-lg border-2 transition-all ${
                active
                  ? "border-rose-500 bg-rose-50/60"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}
              data-testid={`buyer-notification-pref-${opt.value}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon size={14} className={active ? "text-rose-600" : "text-slate-500"} />
                <span className="font-bold text-sm">{opt.label}</span>
              </div>
              <div className="text-[10px] text-[var(--ink-3)]">{opt.hint}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
